import React, { useRef, useState } from 'react';
import { withStyles, Typography, Button, Tooltip, IconButton } from '@material-ui/core';
import { connect } from 'react-redux';
import moment from "moment";
import PrintQueueList from './PrintQueueList';
import { hasPermission, createHistory, getProductData, formatDate, hasAtLeastOnePermission } from '../../sc-utilities';
import { CloudDownload, Autorenew, Markunread, PrintDisabled } from '@material-ui/icons';
import * as actions from './store/printQueueActions';
import { Redirect } from 'react-router-dom';
import { download } from '../../sc-utilities/download.utils';
import config from "../../../config/config";
import { snackActions } from "../../sc-utilities/SnackBar";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: 'none',
            margin: theme.spacing(),
            minWidth: '24px',
            borderRadius: '1px',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        "[class*='printing']": {
            color: theme.palette.info.dark
        },
        "[class*='failed']": {
            color: theme.palette.error.dark
        },
        "[class*='pending']": {
            color: theme.palette.secondary.main
        },
        "[class*='printed']": {
            color: theme.palette.success.main
        },
    },
    paletteButtons: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.buttonText.default,
        margin: theme.spacing(),
        minWidth: '24px',
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    printing: {
        color: theme.palette.info.dark

    },
    pending: {
        color: theme.palette.secondary.main

    },
    failed: {
        color: theme.palette.error.dark,

    },
    printed: {
        color: theme.palette.success.main

    },
    button: {
        textTransform: 'none',
        textDecoration: 'none'
    },
    statusActive: {
        color: theme.palette.success.dark
    },
    statusInactive: {
        color: theme.palette.error.dark
    },
    remote: {
        textDecoration: 'none',
        color: 'white'
    }
});

const PrintQueue = props => {
    const tableRef = useRef();
    const { classes, translations, permissions, columns, activeCustomer } = props;
    const [open, setOpen] = useState({ open: false, printQueueID: null })

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, productID: null, redirectPath: '' });
    let visible = columns && columns['PrintQueue'];
    const createPaletteAction = (product, paletteType, classes) => {
        return product[`${paletteType}`] ?
            <Button className={classes['paletteButtons']} key={`${product[`${paletteType}`][`${paletteType}InventoryID`]}@${moment().valueOf()}`} variant="contained" color="primary"
                value={product[paletteType][`${paletteType}InventoryID`]} size='small' onClick={event => {
                    switch (paletteType) {
                        case 'Colli':
                            return hasPermission(permissions, 'colli_list') ? goToColli(event) : null
                        default:
                            return null;
                    }
                }}>
                {product[paletteType][`${paletteType}InventoryID`]}
            </Button> : translations['short_not_assigned'] || 'n/a';
    }

    const goToColli = event => {
        let colliInventoryID = event.currentTarget.value;
        createHistory();
        setRedirectOptions({
            redirect: true,
            colliInventoryID: event.currentTarget.value,
            redirectPath: `/logistic/container?ColliInventoryID=${colliInventoryID}`
        });
    }

    const goToMobileProduct = event => {
        let mobileProductInventoryID = event.currentTarget.value;
        createHistory();
        setRedirectOptions({
            redirect: true,
            mobileProductInventoryID: event.currentTarget.value,
            redirectPath: `/products/mobile-products?InventoryID=${mobileProductInventoryID}`
        });
    }

    const goToPcProduct = event => {
        let pcProductInventoryID = event.currentTarget.value;
        createHistory();
        setRedirectOptions({
            redirect: true,
            pcProductInventoryID: event.currentTarget.value,
            redirectPath: `/products/pc-products?InventoryID=${pcProductInventoryID}`
        });
    }
    const goToStorageProduct = event => {
        let storageProductInventoryID = event.currentTarget.value;
        createHistory();
        setRedirectOptions({
            redirect: true,
            storageProductInventoryID: event.currentTarget.value,
            redirectPath: `/products/storage-products?InventoryID=${storageProductInventoryID}`
        });
    }

    const createPaletteActionForProduct = (data, productType, classes) => {
        return data ?
            <Button className={classes['paletteButtons']} key={data} variant="contained" value={data} size='small'
                onClick={event => {
                    switch (productType) {
                        case 'MobileProduct':
                            return hasPermission(permissions, 'product_list') ? goToMobileProduct(event) : null
                        case 'PCProduct':
                            return hasPermission(permissions, 'product_list') ? goToPcProduct(event) : null
                        case 'StorageProduct':
                            return hasPermission(permissions, 'product_list') ? goToStorageProduct(event) : null
                        default:
                            return null;
                    }
                }}>
                {data}
            </Button> : translations['short_not_assigned'] || 'n/a';
    }

    const action = (status, printQueueID) => {
        if (status < -1) {
            return <Tooltip key={`${printQueueID}-Retry`} title={translations["retry"] || "Retry"}>
                <IconButton
                    component='div'
                    onClick={() => handleRePrint(printQueueID)}
                >
                    <Autorenew />
                </IconButton>
            </Tooltip>

        }
        if (status === -1) {
            return <Tooltip key={`${printQueueID}-printing`} title={translations["printing"] || "Printing"}>
                <IconButton
                    component='div'
                    disabled={true}
                >
                    <PrintDisabled />
                </IconButton>
            </Tooltip>
        }
        if (status === 0) {
            return <Tooltip key={`${printQueueID}-print`} title={translations["re-print"] || "Re-print"}>
                <IconButton
                    component='div'
                    onClick={() => handleRePrint(printQueueID)}
                >
                    <Autorenew />
                </IconButton>
            </Tooltip>

        }
        if (status > 0) {
            return <Tooltip key={`${printQueueID}-pending`} title={translations["pending"] || "Pending"}>
                <IconButton
                    component='div'
                    disabled={true}
                >
                    <Markunread />
                </IconButton>
            </Tooltip>
        }
        return <></>;
    }

    let tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 200, maxWidth: 200 },
            filtering: false,
            render: rowData => {
                return (<>
                    {hasPermission(permissions, 'product_print_label') ? action(rowData.Status, rowData.PrintJobID) : null}
                    {
                        hasPermission(permissions, 'print_job_download') ?
                            <Tooltip key={`${rowData.PrintJobID}-Download`} title={translations["download_printer_queue"] || "Download printer queue"}>
                                <IconButton
                                    component='div'
                                    onClick={(event) => handleDownload(rowData.PrintJobID)}
                                >
                                    <CloudDownload />
                                </IconButton>
                            </Tooltip> : null
                    }
                </>
                );
            }
        },
        {
            title: translations["last_modified_at"] || 'Last modified at', field: 'LastModifiedAt',
            hidden: visible ? visible['LastModifiedAt'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.LastModifiedAt ? formatDate(rowData.LastModifiedAt) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["colli_id"] || 'Colli ID', field: 'ColliInventoryID',
            hidden: visible ? visible['ColliInventoryID'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Product && rowData.Product.Colli && rowData.Product.Colli.ColliInventoryID ? createPaletteAction(rowData.Product, 'Colli', classes) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["inventory_id"] || 'Securaze ID', field: 'ProductInventoryID',
            hidden: visible ? visible['ClientVersion'] : false,
            render: rowData => {
                return rowData.Product.InventoryID ? createPaletteActionForProduct(rowData.Product.InventoryID, rowData.Product.Type, classes) : translations['short_not_assigned'] || 'n/a'
            }
        },
        {
            title: translations["serial_number"] || 'Serial number', field: 'SerialNumber',
            hidden: visible ? visible['SerialNumber'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{getProductData(rowData, 'SerialNumber')}</Typography>;
            }
        },
        {
            title: translations["product_model"] || 'Product model', field: 'ProductModel',
            hidden: visible ? visible['ProductModel'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Product && rowData.Product.Type ? rowData.Product.Type : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["printer_type"] || 'Printer type', field: 'PrinterType',
            hidden: visible ? visible['PrinterType'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Printer ? rowData.Printer.PrinterType : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["connection_type"] || 'Connection Type', field: 'ConnectionType',
            hidden: visible ? visible['ConnectionType'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Printer ? rowData.Printer.ConnectionType : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["printer_name"] || 'Printer name', field: 'PrinterName',
            hidden: visible ? visible['PrinterName'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Printer ? rowData.Printer.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["uri"] || 'URI', field: 'URI',
            hidden: visible ? visible['URI'] : false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Printer ? rowData.Printer.URI : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["original_label"] || 'Original label', field: 'OriginalLabel',
            hidden: visible ? visible['OriginalLabel'] : false,
            lookup: { '1': translations['original'] || 'Original', '0': translations['copy'] || 'Copy' },
            defaultFilter: ['1', '0'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.OriginalLabel ? translations['original'] || 'Original' : translations['copy'] || 'Copy'}</Typography>;
            }
        },
        {
            title: translations["status"] || 'Status', field: 'Status',
            hidden: visible ? visible['Status'] : false,
            lookup: { '1': translations['pending'] || 'Pending', '0': translations['printed'] || 'Printed', '-2': translations['failed'] || "Failed", '-1': translations['printing'] || 'Printing' },
            defaultFilter: ['1', '0', '-2', '-1'],
            render: rowData => {
                return <Typography className={classes[rowData.Status === 1 ? "pending" : rowData.Status === 0 ? 'printed' : rowData.Status === -2 ? 'failed' : 'printing']} variant={'caption'}>{rowData.Status === 1 ? translations['pending_print_queue'] || "PENDING" : rowData.Status === 0 ? translations['printed_print_queue'] || 'PRINTED' : rowData.Status === -2 ? translations['failed_print_queue'] || 'FAILED' : translations['printing_print_queue'] || 'PRINTING'}</Typography>;
            }
        }
    ];

    if (!hasAtLeastOnePermission(permissions, [
        "download_result",
        "product_print_label"
    ])) {
        tableColumns.shift();
    }
    const handleDownload = async (printerID,) => {
        const objectURL = `${config.backend_domain}:${config.backend_port}/print_jobs/download?PrintJobID=${printerID}&ActiveCustomerID=${activeCustomer}`;
        let exportDataSuccessful = await download(objectURL);
        tableRef.current && tableRef.current.onQueryChange();
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
    }

    const handleRePrint = async printQueueID => {
        setOpen({ open: true, printQueueID: printQueueID })
    }

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'print_job_list') ?
                <PrintQueueList open={open} setOpen={setOpen} tableColumns={tableColumns} tableRef={tableRef} /> : null}
        </div >
    );
}

const mapStateToProps = state => {
    return {
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onRePrint: (printQueueID, id) => dispatch(actions.rePrint(printQueueID, id))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(PrintQueue));